import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Article } from '../types'
import * as api from '../api/tecdocApi'
import type { ApiError } from '../types/api'
import { useVehicleStore } from './vehicleStore'

export const usePartsStore = defineStore('parts', () => {
  const articles = ref<Article[]>([])
  const isLoadingParts = ref(false)
  const partsError = ref<string | null>(null)

  // Pull fitment inputs from the vehicle store at call time so a later regnr
  // lookup that populates VIN immediately reflects on subsequent parts calls.
  function fitmentInputs() {
    const vs = useVehicleStore()
    return { vin: vs.selectedVin, motorCode: vs.selectedMotorCode }
  }

  async function loadRmiParts(qualId: string, carId: number, mainGroupId: string, subGroupId: string, itemId: number) {
    isLoadingParts.value = true
    partsError.value = null
    articles.value = []

    try {
      const { vin, motorCode } = fitmentInputs()
      articles.value = await api.getRmiProducts(qualId, carId, mainGroupId, subGroupId, itemId, vin, motorCode)
    } catch (err) {
      partsError.value = (err as ApiError).message
    } finally {
      isLoadingParts.value = false
    }
  }

  async function loadCategoryParts(carId: number, catId: number) {
    isLoadingParts.value = true
    partsError.value = null
    articles.value = []

    try {
      const { vin, motorCode } = fitmentInputs()
      articles.value = await api.getCategoryParts(carId, catId, vin, motorCode)
    } catch (err) {
      partsError.value = (err as ApiError).message
    } finally {
      isLoadingParts.value = false
    }
  }

  async function loadOemParts(oeNum: string) {
    isLoadingParts.value = true
    partsError.value = null
    articles.value = []

    try {
      const { vin, motorCode } = fitmentInputs()
      articles.value = await api.searchByOem(oeNum, vin, motorCode)
    } catch (err) {
      partsError.value = (err as ApiError).message
    } finally {
      isLoadingParts.value = false
    }
  }

  function reset() {
    articles.value = []
    partsError.value = null
  }

  return {
    articles,
    isLoadingParts,
    partsError,
    loadRmiParts,
    loadCategoryParts,
    loadOemParts,
    reset,
  }
})
